import { useEffect, useRef, useState, useCallback } from 'react';
import { makeStyles, mergeClasses, Tooltip } from '@coreai/fluentui-react';
import { telemetryService } from '@/services/telemetryService';

export type ClampedLabelProps = {
  text: string;
  /** Number of lines to clamp to. */
  lines: 1 | 2;
  className?: string;
};

/**
 * ClampedLabel component displays text with ellipsis truncation.
 *
 * Features:
 * - Clamps text to 1 or 2 lines with ellipsis overflow
 * - Automatically detects truncation and shows a tooltip with full text
 * - Monitors element resize to handle canvas zoom and layout changes
 * - Tracks truncation events for telemetry (no PII)
 *
 * @example
 * <ClampedLabel text="Long node name" lines={1} />
 * <ClampedLabel text="Multi-line description" lines={2} className="custom-class" />
 */
const useStyles = makeStyles({
  clamp1: {
    display: 'block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  clamp2: {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    hyphens: 'auto',
  },
});

export const ClampedLabel = ({ text, lines, className }: ClampedLabelProps) => {
  const styles = useStyles();
  const spanRef = useRef<HTMLSpanElement>(null);
  const [truncated, setTruncated] = useState(false);
  const prevTruncatedRef = useRef(false);

  const checkTruncation = useCallback(() => {
    const el = spanRef.current;
    if (!el) return;
    // For 1-line clamps the text is laid out with `whiteSpace: nowrap` and
    // overflows horizontally, so height stays equal — we must compare width.
    // For 2-line clamps `-webkit-line-clamp` overflows vertically. The +1
    // absorbs sub-pixel rounding differences in both axes.
    const isTruncated =
      lines === 1
        ? el.scrollWidth > el.clientWidth + 1
        : el.scrollHeight > el.clientHeight + 1;
    if (isTruncated === prevTruncatedRef.current) return;

    setTruncated(isTruncated);
    prevTruncatedRef.current = isTruncated;

    // Telemetry: track transition to truncated — no PII, no label content
    if (isTruncated) {
      telemetryService.trackEvent('workflow.node.label.truncated', undefined, {
        lines,
        labelLength: text.length,
      });
    }
  }, [lines, text]);

  // Re-evaluate when text changes
  useEffect(() => {
    checkTruncation();
  }, [text, checkTruncation]);

  // Re-evaluate on resize (canvas zoom changes effective rendered size)
  useEffect(() => {
    const el = spanRef.current;
    if (!el) return;
    const observer = new ResizeObserver(checkTruncation);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkTruncation]);

  const spanClass = mergeClasses(lines === 1 ? styles.clamp1 : styles.clamp2, className);

  // When truncated, the span itself becomes the Tooltip trigger. Fluent's
  // Tooltip listens for both pointerover AND focus on its child, so we must
  // make the span focusable so keyboard-only users can reveal the full
  // text — WCAG 1.4.13 (Content on Hover/Focus).
  const span = (
    <span
      ref={spanRef}
      className={spanClass}
      data-truncated={truncated || undefined}
      {...(truncated && { tabIndex: 0 })}
    >
      {text}
    </span>
  );

  if (truncated) {
    return (
      <Tooltip content={text} relationship="label" positioning="above" withArrow>
        {span}
      </Tooltip>
    );
  }

  return span;
};
